import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet, fetchApiPost, fetchApiPut, fetchApiDelete } from "../../utils";
import { BaseDbObject } from "@/lib/models/db";

export const CONSTRAINT_TYPE_LABELS = [{
    value: "hedged_percent",
    label: "Hedged Percent"
}];

export function getConstraintDisplayLabel(value: string) {
    return CONSTRAINT_TYPE_LABELS.find(item => item.value === value)?.label || value;
}

// Query parameter validation schema
const ListRecommendationsQuerySchema = z.object({
  date: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Date must be in YYYY-MM-DD format").optional(),
  startDate: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Start date must be in YYYY-MM-DD format").optional(),
  endDate: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "End date must be in YYYY-MM-DD format").optional(),
  cropYear: z.string().regex(/^\d+$/).transform(Number).optional(),
  commodityType: z.string().optional(),
}).refine(
  (data) => !(data.date && (data.startDate || data.endDate)),
  {
    message: "Cannot provide both date and startDate/endDate simultaneously",
    path: ["date"],
  }
);

// Request body validation schemas
const RecommendationRuleSchema = z.object({
  rangeType: z.enum(["<", "<=", ">", ">=", "between"]),
  constraintType: z.enum(["hedged_percent"]),
  rangeConstraintValue: z.string().min(1, "Range constraint value cannot be empty"),
  message: z.string().min(1, "Message cannot be empty").trim(),
});

const CreateRecommendationBodySchema = z.object({
  recommendationDate: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Recommendation date must be in YYYY-MM-DD format"),
  cropYear: z.number().int(),
  commodityType: z.string().min(1, "Commodity type cannot be empty"),
  rules: z.array(RecommendationRuleSchema).min(1, "At least one rule is required"),
}).refine(
  (data) => {
    // All rules must have the same constraintType
    const constraintTypes = new Set(data.rules.map(r => r.constraintType));
    return constraintTypes.size === 1;
  },
  {
    message: "All rules must have the same constraintType",
    path: ["rules"],
  }
);

const UpdateRecommendationBodySchema = z.object({
  rules: z.array(RecommendationRuleSchema).min(1, "At least one rule is required"),
}).refine(
  (data) => {
    // All rules must have the same constraintType
    const constraintTypes = new Set(data.rules.map(r => r.constraintType));
    return constraintTypes.size === 1;
  },
  {
    message: "All rules must have the same constraintType",
    path: ["rules"],
  }
);

// TypeScript interfaces
export interface RecommendationRule {
  id: string;
  created_at: Date;
  updated_at: Date;
  recommendation_group_id: string;
  range_type: "<" | "<=" | ">" | ">=" | "between";
  constraint_type: "hedged_percent";
  range_constraint_value: string;
  message: string;
}

export interface RecommendationGroup extends BaseDbObject {
  recommendation_date: Date;
  crop_year: number;
  commodity_type: string;
  RecommendationRules: RecommendationRule[];
}

export interface ListRecommendationsResponse {
  data: RecommendationGroup[];
}

export interface CreateRecommendationResponse {
  data: RecommendationGroup;
}

export interface UpdateRecommendationResponse {
  data: RecommendationGroup;
}

// Helper function to assemble query parameters
function assembleListQueryParams(filters?: Partial<z.infer<typeof ListRecommendationsQuerySchema>>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.date) {
    queryParams.append("date", filters.date);
  }

  if (filters.startDate) {
    queryParams.append("startDate", filters.startDate);
  }

  if (filters.endDate) {
    queryParams.append("endDate", filters.endDate);
  }

  if (filters.cropYear !== undefined) {
    queryParams.append("cropYear", filters.cropYear.toString());
  }

  if (filters.commodityType) {
    queryParams.append("commodityType", filters.commodityType);
  }

  return queryParams;
}

// React Query hooks
export function useListRecommendations(filters?: Partial<z.infer<typeof ListRecommendationsQuerySchema>>) {
  const queryParams = assembleListQueryParams(filters);

  return useQuery({
    queryKey: ["risk", "recommendations", queryParams.toString()],
    queryFn: () => fetchApiGet<ListRecommendationsResponse>("risk/recommendations", queryParams),
    retry: 1,
  });
}

export function useCreateRecommendation() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateRecommendationBodySchema>) => {
    const result = await fetchApiPost<CreateRecommendationResponse>(
      "risk/recommendations",
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "recommendations"] });
    }

    return result;
  };
}

export function useUpdateRecommendation() {
  const queryClient = useQueryClient();

  return async (
    recommendationDate: string,
    cropYear: number,
    commodityType: string,
    recommendationGroupId: string,
    data: z.infer<typeof UpdateRecommendationBodySchema>
  ) => {
      const queryParams = new URLSearchParams();
      queryParams.set("recommendationDate", recommendationDate);
      queryParams.set("cropYear", cropYear.toString());
      queryParams.set("commodityType", commodityType);
      queryParams.set("recommendationGroupId", recommendationGroupId);

    const result = await fetchApiPut<UpdateRecommendationResponse>(
      `risk/recommendations/update?${queryParams.toString()}`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "recommendations"] });
    }

    return result;
  };
}

export function useCopyLastRecommendations() {
  const queryClient = useQueryClient();

  return async () => {
    const result = await fetchApiPost<ListRecommendationsResponse>(
      "risk/recommendations/copy/last",
      {},
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "recommendations"] });
    }

    return result;
  };
}

export function useDeleteRecommendation() {
  const queryClient = useQueryClient();

  return async (
    recommendationDate: string,
    cropYear: number,
    commodityType: string
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.set("recommendationDate", recommendationDate);
    queryParams.set("cropYear", cropYear.toString());
    queryParams.set("commodityType", commodityType);

    const result = await fetchApiDelete(
      `risk/recommendations?${queryParams.toString()}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "recommendations"] });
    }

    return result;
  };
}
